/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/
import { fetchBaseQuery } from "@reduxjs/toolkit/query";
// eslint-disable-next-line no-duplicate-imports
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError
} from "@reduxjs/toolkit/query";
import { Mutex } from "async-mutex";
import { runtimeEnv } from "configuration";
import { AuthManager } from "services/auth-service/auth";
import { removeCredentials } from "store/features";
import { RootState, store } from "store/reduxStore";

// create a new mutex
const mutex = new Mutex();

// create base query with authorization header
const baseQuery = fetchBaseQuery({
  baseUrl: `${runtimeEnv.REACT_APP_SANDBOX_BACKEND_URL}/api/`,
  prepareHeaders: (headers, { getState }) => {
    // By default, if we have a token in the store, let's use that for authenticated requests
    const accessToken = (getState() as RootState).auth.accessToken;
    if (accessToken) {
      headers.set("authorization", `Bearer ${accessToken}`);
    }
    return headers;
  },
});

export const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // checking whether the mutex is locked or there is no known silent sign-in error
    if (!mutex.isLocked() && !store.getState().auth.silentSigninError) {
      const release = await mutex.acquire();
      try {
        const tokenAcquired = await AuthManager.silentsignIn();

        if (tokenAcquired) {
          // retry the initial query
          result = await baseQuery(args, api, extraOptions);
        } else {
          api.dispatch(removeCredentials());
        }
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      // ??? is it needed to retry here ???
      // result = await baseQuery(args, api, extraOptions);
    }
  }
  return result;
};
